import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { AcessoDoMembro, MembroDaEquipe, VagasDeMembros } from '@/data/tipos';
import { pode } from '@/session/sessao';
import { EditarUsuarioDaEquipeModal } from './EditarUsuarioDaEquipeModal';
import { NovoUsuarioDaEquipeModal } from './NovoUsuarioDaEquipeModal';

const ACESSO: Record<AcessoDoMembro, { rotulo: string; tom: 'success' | 'warning' | 'neutral' }> = {
    'ativo': { rotulo: 'Ativo', tom: 'success' },
    'convite-pendente': { rotulo: 'Convite pendente', tom: 'warning' },
    'desativado': { rotulo: 'Desativado', tom: 'neutral' },
};

/**
 * Quem tem acesso à clínica.
 *
 * É a MESMA lista da aba Profissionais de Contatos, lida pelo mesmo caminho:
 * duas listas de quem trabalha aqui discordariam na primeira contratação. O que
 * muda é a pergunta — lá é cadastro, aqui é acesso.
 */
export function EquipeDaClinica({ equipe, vagas }: { equipe: MembroDaEquipe[]; vagas: VagasDeMembros }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [emCurso, setEmCurso] = useState<string | null>(null);
    const [criando, setCriando] = useState(false);
    const [editando, setEditando] = useState<MembroDaEquipe | null>(null);
    /* A permissão `equipe` só vem de `*` (proprietária) ou de concessão
       explícita (0037). Esconder o botão é cortesia; quem recusa é o banco. */
    const gerenciaEquipe = pode('equipe');
    /* Vagas do plano (0165). A tela avisa e some com o botão; quem recusa de
       verdade é o gatilho do banco — e é por isso que o aviso pode estar
       desatualizado sem virar bug: o pedido volta com a recusa certa. */
    const lotado = vagas.limite !== null && vagas.ocupadas >= vagas.limite;
    const vagasNoTexto = vagas.limite === null
        ? `${vagas.ocupadas} ${vagas.ocupadas === 1 ? 'pessoa' : 'pessoas'} com acesso`
        : `${vagas.ocupadas} de ${vagas.limite} ${vagas.limite === 1 ? 'vaga' : 'vagas'} do plano`;

    const [confirmar, confirmacao] = useConfirmacao();

    const trocarAcesso = async (membro: MembroDaEquipe, acesso: AcessoDoMembro) => {
        if (acesso === 'desativado') {
            const ok = await confirmar({
                titulo: `Desativar o acesso de ${membro.nome}?`,
                descricao: 'A pessoa deixa de entrar na Fórmula MED. O cadastro continua na lista, marcado como desativado.',
                confirmar: 'Desativar acesso',
            });
            if (!ok) return;
        }
        setEmCurso(membro.id);
        try {
            await dados().atualizarMembro(membro.id, { acesso });
            aviso({
                title: acesso === 'desativado' ? 'Acesso removido' : 'Acesso liberado',
                description: membro.nome,
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({
                title: 'Não foi possível mudar o acesso',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setEmCurso(null);
        }
    };

    const colunas: DataTableColumn<MembroDaEquipe>[] = [
        {
            key: 'nome', header: 'Pessoa', sortable: true,
            render: (m) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{m.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {[m.papel, m.registro].filter(Boolean).join(' · ')}
                    </span>
                </span>
            ),
        },
        { key: 'especialidades', header: 'Especialidades' },
        { key: 'telefone', header: 'Telefone', width: 160 },
        {
            key: 'acesso', header: 'Acesso', width: 170,
            render: (m) => <Badge tone={ACESSO[m.acesso].tom}>{ACESSO[m.acesso].rotulo}</Badge>,
        },
        {
            key: 'acoes', header: '', width: gerenciaEquipe ? 210 : 130, align: 'right',
            render: (m) => (
                <span style={{ display: 'inline-flex', gap: 8 }}>
                    {gerenciaEquipe && (
                        <Button size="sm" variant="ghost" disabled={emCurso === m.id} onClick={() => setEditando(m)}>Editar</Button>
                    )}
                    <Button
                        size="sm" variant="secondary" disabled={emCurso === m.id}
                        onClick={() => trocarAcesso(m, m.acesso === 'desativado' ? 'ativo' : 'desativado')}
                    >
                        {m.acesso === 'desativado' ? 'Reativar' : 'Desativar'}
                    </Button>
                </span>
            ),
        },
    ];

    const pendentes = equipe.filter((m) => m.acesso === 'convite-pendente');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {confirmacao}
            <NovoUsuarioDaEquipeModal open={criando} onClose={() => setCriando(false)} onCriado={() => revalidator.revalidate()} />
            <EditarUsuarioDaEquipeModal membro={editando} onClose={() => setEditando(null)} onSalvo={() => revalidator.revalidate()} />
            {gerenciaEquipe && lotado && (
                <Alert tone="warning" title="As vagas do plano acabaram">
                    {`O plano desta clínica libera ${vagas.limite} ${vagas.limite === 1 ? 'pessoa' : 'pessoas'} com acesso, e ${vagas.ocupadas === 1 ? 'já há 1' : `já são ${vagas.ocupadas}`}. Convite pendente também ocupa vaga. Para cadastrar mais alguém, desative quem não usa mais ou peça mais vagas à plataforma.`}
                </Alert>
            )}
            {pendentes.length > 0 && (
                <Alert tone="info" title={`${pendentes.length} convite${pendentes.length > 1 ? 's' : ''} pendente${pendentes.length > 1 ? 's' : ''}`}>
                    {/* O vínculo entre login e membro é feito pelo e-mail, num
                        gatilho do banco: quem aceita o convite vira "ativo"
                        sozinho. Reenviar convite exige e-mail transacional, que
                        esta clínica ainda não tem ligado — por isso a tela conta
                        o que falta em vez de oferecer um botão que não envia. */}
                    {pendentes.map((m) => m.nome).join(', ')} — o acesso é liberado quando a pessoa entra
                    pela primeira vez com o e-mail cadastrado.
                </Alert>
            )}

            <Card
                title="Equipe e acessos"
                subtitle={`A mesma lista de Contatos › Profissionais. Aqui se decide quem entra — clique na pessoa para abrir o cadastro. ${vagasNoTexto}.`}
                padding={0}
                actions={gerenciaEquipe
                    ? <Button size="sm" onClick={() => setCriando(true)} disabled={lotado}>Novo usuário</Button>
                    : undefined}
            >
                <DataTable
                    columns={colunas} rows={equipe} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma pessoa cadastrada."
                    /* Clicar na linha abre o cadastro da pessoa. O botão "Editar"
                       continua na linha para quem procura o comando à vista; o
                       `DataTable` já ignora o clique que nasceu num controle
                       interno, então os dois não disparam juntos. Sem permissão de
                       equipe, a linha não abre nada — o modal edita, e o banco
                       recusaria. */
                    onRowClick={gerenciaEquipe ? (m) => setEditando(m) : undefined}
                    rowLabel={(m) => m.nome}
                />
            </Card>
        </div>
    );
}
